import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLandingPageViewModel, GAME_MODES } from '../hooks/useLandingPageViewModel';
import { useDailyRewardsViewModel } from '@/features/daily-rewards/hooks/useDailyRewardsViewModel';
import { DailyRewardsBottomSheet } from '@/features/daily-rewards/components/DailyRewardsBottomSheet';
import { usePageSEO } from '@/shared/hooks/usePageSEO';
import { GAME_JSON_LD } from '@/shared/seo';
import {
  ludokaLogo,
  settingIcon,
  coinImg as coinIcon,
  yasGreenLogo,
  yasFlag,
  trophyIcon,
  shopIcon,
  rewardIcon
} from '@/assets';
import { PreMaintenanceBanner, MaintenancePage } from '@/features/maintenance';
import { GameModeCard } from '@/shared/components/GameModeCard';
import { BottomSheet } from '@/shared/components/BottomSheet';
import { TicketCard } from '@/shared/components/TicketCard';
import { Button } from '@/shared/components/Button';
import { Flag } from '@/shared/components/Flag';
import { AnimatePresence } from 'framer-motion';
import { WaitingPlayersBadge } from './WaitingPlayersBadge';
import { PrivateLobbyDialog } from './PrivateLobbyDialog';
import { PointsAnnouncementDialog } from './PointsAnnouncementDialog';
import { JoinRoomDialog } from './JoinRoomDialog';
import { SettingsDialog } from './SettingsDialog';
import { RejoinGameDialog } from './RejoinGameDialog';
import { MatchmakingDialog } from './MatchmakingDialog';
import { InsufficientPointsDialog } from '@/shared/components/InsufficientPointsDialog';
import { PaidMatchConfirmationDialog } from '@/shared/components/PaidMatchConfirmationDialog';
import { LeaderboardBottomSheet } from '@/features/leaderboard';
import { SkinPickerSheet } from '@/features/skins';
import { createResponsiveTextStroke } from '@/styles/textStroke';
import { getCountryCode } from '@/lib/country-codes';

export default function LandingPage() {
  const { t } = useTranslation();
  const { state, actions } = useLandingPageViewModel();
  const {
    state: dailyState,
    actions: dailyActions,
    onMount: dailyOnMount
  } = useDailyRewardsViewModel();
  useEffect(dailyOnMount, []);
  const seoElement = usePageSEO({
    title: t('seo.home.title'),
    description: t('seo.home.description'),
    canonicalPath: '/',
    noSuffix: true,
    jsonLd: GAME_JSON_LD
  });

  return (
    <div className="flex-1 w-full relative flex items-center justify-center">
      {seoElement}
      {/* Pre-Maintenance Banner - Shown when maintenance is preparing */}
      {state.isMaintenancePreparing && <PreMaintenanceBanner />}

      {/* Yas Green Logo - Above LUDOKA Logo */}
      <div className="absolute top-[40%] left-1/2 transform -translate-x-1/2 -translate-y-full">
        <img
          src={yasGreenLogo}
          alt="Yas"
          className="w-[20vw] min-w-24 max-w-32 h-auto object-contain"
        />
      </div>

      {/* Yas Flags - Left Side Spectators */}
      {/* Top Section */}
      <img
        src={yasFlag}
        alt=""
        className="absolute top-[3vh] left-2 w-[4vw] min-w-10 max-w-14 h-auto object-contain opacity-85 rotate-40"
      />
      <img
        src={yasFlag}
        alt=""
        className="absolute top-[15vh] left-2 w-[4.5vw] min-w-12 max-w-16 h-auto object-contain opacity-90 -rotate-6"
      />
      <img
        src={yasFlag}
        alt=""
        className="absolute top-[28vh] left-3 w-[3.5vw] min-w-8 max-w-12 h-auto object-contain opacity-80 rotate-[8deg]"
      />

      {/* Bottom Section */}
      <img
        src={yasFlag}
        alt=""
        className="absolute bottom-[28vh] left-3 w-[3.5vw] min-w-8 max-w-12 h-auto object-contain opacity-80 -rotate-12"
      />
      <img
        src={yasFlag}
        alt=""
        className="absolute bottom-[15vh] left-3 w-[4.5vw] min-w-12 max-w-16 h-auto object-contain opacity-90 rotate-6"
      />
      <img
        src={yasFlag}
        alt=""
        className="absolute bottom-[3vh] left-[8vw] w-[4vw] min-w-10 max-w-14 h-auto object-contain opacity-85 -rotate-[10deg]"
      />

      {/* Yas Flags - Right Side Spectators */}
      {/* Top Section */}
      <img
        src={yasFlag}
        alt=""
        className="absolute top-[3vh] right-[5vw] w-[4vw] min-w-10 max-w-14 h-auto object-contain opacity-85 -scale-x-150 rotate-260"
      />
      <img
        src={yasFlag}
        alt=""
        className="absolute top-[15vh] right-3 w-[4.5vw] min-w-12 max-w-16 h-auto object-contain opacity-90 -scale-x-100 -rotate-6"
      />
      <img
        src={yasFlag}
        alt=""
        className="absolute top-[28vh] right-3 w-[3.5vw] min-w-8 max-w-12 h-auto object-contain opacity-80 -scale-x-100 rotate-[8deg]"
      />

      {/* Bottom Section */}
      <img
        src={yasFlag}
        alt=""
        className="absolute bottom-[28vh] right-3 w-[3.5vw] min-w-8 max-w-12 h-auto object-contain opacity-80 -scale-x-150 -rotate-12"
      />
      <img
        src={yasFlag}
        alt=""
        className="absolute bottom-[15vh] right-1 w-[4.5vw] min-w-12 max-w-16 h-auto object-contain opacity-90 -scale-x-100 rotate-6"
      />
      <img
        src={yasFlag}
        alt=""
        className="absolute bottom-[3vh] right-3 w-[4vw] min-w-10 max-w-14 h-auto object-contain opacity-85 -scale-x-100 -rotate-[10deg]"
      />

      {/* Flag and User Info - Top Left */}
      <div className="absolute top-20 left-0 flex flex-col">
        <Flag
          countryCode={getCountryCode(state.userCountry)}
          size="lg"
          alt={`${state.userCountry ?? 'Madagascar'} flag`}
          className="ml-[-10px]"
        />

        {/* User Info Panel - Username left, Points + coin right */}
        <div className="bg-black/40 backdrop-blur-md px-3 border border-white/10 flex items-center justify-between gap-4 mt-[-5px]">
          {/* Username - Left */}
          <span
            className="font-mind-boggle text-[#F2F2F2] text-sm leading-none"
            style={createResponsiveTextStroke(0.5, 0.04)}
          >
            {state.userPseudo ?? 'GUEST'}
          </span>

          {/* Points + Coin - Right */}
          <div className="flex items-center gap-1">
            <span
              className="font-mind-boggle text-[#FCD34D] text-base leading-none"
              style={createResponsiveTextStroke(0.5, 0.04)}
            >
              {state.userPoints.toLocaleString()}
            </span>
            <img src={coinIcon} alt="coins" className="w-5 h-5 object-contain" />
          </div>
        </div>
      </div>

      {/* Settings & Leaderboard buttons - Top Right */}
      <div className="absolute top-20 right-10 flex flex-col gap-2">
        <Button
          variant="blue"
          onClick={actions.openSettingsDialog}
          className="w-16 h-16"
          aria-label={t('accessibility.settings')}
        >
          <img src={settingIcon} alt="" className="w-10 h-10 object-contain" />
        </Button>
        <Button
          variant="blue"
          onClick={actions.openLeaderboard}
          className="w-16 h-16"
          aria-label={t('leaderboard.title')}
        >
          <img src={trophyIcon} alt="" className="w-10 h-10 object-contain" />
        </Button>
        {/* Daily Rewards button — opens the rank/position + podium sheet. */}
        <div className="relative">
          <Button
            variant="blue"
            onClick={actions.openDailyRewards}
            className="w-16 h-16"
            aria-label={t('accessibility.dailyRewards')}
          >
            <img
              src={rewardIcon}
              alt=""
              className="w-10 h-10 object-contain"
              style={{ filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.4))' }}
            />
          </Button>
          {dailyState.isRewardsEnabled && dailyState.claimable && (
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white/80 z-50 animate-pulse" />
          )}
        </div>
        <div className="relative">
          <Button
            variant="blue"
            onClick={actions.openShop}
            className="w-16 h-16"
            aria-label={t('landing.shop')}
          >
            <img src={shopIcon} alt="" className="w-10 h-10 object-contain" />
          </Button>
          {state.showShopNewBadge && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none z-50 animate-pulse">
              NEW
            </span>
          )}
        </div>
      </div>

      {/* Center content - Logo */}
      <img src={ludokaLogo} alt="Ludoka Logo" className="max-w-full max-h-full object-contain" />

      {/* Waiting Players Toasts - Right Side */}
      <div className="absolute right-0 bottom-56 flex flex-col items-end gap-2 z-10">
        <AnimatePresence>
          {(state.waitingCounts?.free ?? 0) > 0 && (
            <WaitingPlayersBadge
              key="free-waiting"
              count={state.waitingCounts!.free}
              variant="free"
              onClick={() => {
                actions.selectGameMode('free');
              }}
            />
          )}
          {(state.waitingCounts?.paid ?? 0) > 0 && (
            <WaitingPlayersBadge
              key="paid-waiting"
              count={state.waitingCounts!.paid}
              variant="paid"
              onClick={() => {
                actions.selectGameMode('paid');
              }}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Bottom - Game Mode Card */}
      <div className="absolute bottom-20 left-4 right-4">
        <GameModeCard
          variant={state.gameModeConfig.variant}
          onSkinsClick={actions.openSkinPicker}
          showNewBadge={state.showSkinsNewBadge}
          title={state.gameModeConfig.cardTitle}
          subtitle={state.gameModeConfig.cardSubtitle}
          subtitleIcon={
            state.gameModeConfig.cardSubtitleIcon ? (
              <img
                src={state.gameModeConfig.cardSubtitleIcon}
                alt=""
                className="size-full object-contain"
              />
            ) : undefined
          }
          rightContent={
            <img src={state.gameModeConfig.icon} alt="" className="w-full h-auto object-contain" />
          }
          centerButtonProps={{
            onClick: actions.getCenterButtonClickHandler()
          }}
          rightButtonProps={{ onClick: actions.openGameModeSheet }}
        />
      </div>

      {/* Game Mode Selection Bottom Sheet */}
      <BottomSheet
        isOpen={state.isBottomSheetOpen}
        onClose={actions.closeGameModeSheet}
        title={t('landing.gameMode')}
        className="!p-2"
      >
        <div className="flex flex-col gap-3">
          {GAME_MODES.map((mode) => {
            const config = state.gameModeConfigs[mode];
            return (
              <TicketCard
                key={mode}
                variant={config.variant}
                title={config.listTitle}
                subtitle={config.listSubtitle}
                icon={<img src={config.icon} alt="" className="w-full h-auto object-contain" />}
                onClick={() => {
                  actions.selectGameMode(mode);
                }}
                className="w-full h-[120px]"
              />
            );
          })}
        </div>
      </BottomSheet>

      {/* One-time announcement: private free games no longer award SOKA points */}
      <PointsAnnouncementDialog />

      {/* Private Lobby Creation Dialog */}
      <PrivateLobbyDialog
        isOpen={state.isPrivateLobbyDialogOpen}
        onClose={actions.closePrivateLobbyDialog}
        gameType={state.privateLobbyGameType}
        onToggleGameType={actions.togglePrivateLobbyGameType}
        onCreateRoom={actions.createPrivateLobby}
        lobbyCode={state.lobbyCode}
        onCopyCode={actions.copyLobbyCode}
        isCodeCopied={state.isLobbyCodeCopied}
        players={state.players}
        isLoading={state.isCreatingRoom}
        countdown={state.countdown}
        isStarting={state.isStarting}
        isCreator={state.isCreator}
        onStartNow={actions.startGameNow}
      />

      {/* Join Room Dialog */}
      <JoinRoomDialog
        isOpen={state.isJoinRoomDialogOpen}
        onClose={actions.closeJoinRoomDialog}
        gameType={state.gameType}
        invitationCode={state.invitationCode}
        onInvitationCodeChange={actions.setInvitationCode}
        onJoinRoom={actions.joinRoom}
        players={state.players}
        error={state.roomError}
        isLoading={state.isJoiningRoom}
        countdown={state.countdown}
        isStarting={state.isStarting}
        isCreator={state.isCreator}
        onStartNow={actions.startGameNow}
      />

      {/* Matchmaking Dialog */}
      <MatchmakingDialog
        isOpen={state.isMatchmakingDialogOpen}
        onClose={actions.closeMatchmakingDialog}
        gameType={state.matchmakingGameType ?? 'free'}
        players={state.players}
        isSearching={state.isSearchingMatch}
        countdown={state.countdown}
        isStarting={state.isStarting}
        error={state.roomError}
        matchmakingTimeRemaining={state.matchmakingTimeRemaining}
        shouldRefresh={state.shouldRefreshMatchmaking}
        onRefresh={actions.refreshMatchmaking}
      />

      {/* Settings Dialog */}
      <SettingsDialog
        isOpen={state.isSettingsDialogOpen}
        onClose={actions.closeSettingsDialog}
        onRulesClick={actions.handleRulesClick}
        onFaqClick={actions.handleFaqClick}
        onDisconnect={actions.handleDisconnect}
      />

      {/* Rejoin Game Dialog - Non-closable, shown when user has active game */}
      <RejoinGameDialog
        isOpen={state.hasActiveGame}
        gameType={state.activeGame?.gameType ?? 'free'}
        isLoading={state.isRestoringGame}
        onRejoin={actions.rejoinGame}
        error={state.rejoinError}
      />

      {/* Insufficient Points Dialog - Shown when user tries to join paid game without enough points */}
      <InsufficientPointsDialog
        isOpen={state.isInsufficientPointsDialogOpen}
        onClose={actions.closeInsufficientPointsDialog}
        currentPoints={state.userPoints}
      />

      {/* Paid Match Confirmation Dialog - Shown before joining paid matchmaking or creating paid room */}
      <PaidMatchConfirmationDialog
        isOpen={state.isPaidConfirmationDialogOpen}
        onClose={actions.closePaidConfirmationDialog}
        onConfirm={actions.confirmPaidAction}
        currentPoints={state.userPoints}
      />

      {/* Skin Picker Bottom Sheet */}
      <SkinPickerSheet
        isOpen={state.isSkinPickerOpen}
        onClose={actions.closeSkinPicker}
        onOpenShop={actions.openShop}
      />

      {/* Leaderboard Bottom Sheet */}
      <LeaderboardBottomSheet isOpen={state.isLeaderboardOpen} onClose={actions.closeLeaderboard} />

      {/* Daily Rewards Bottom Sheet — rank/position today + match progress + podium */}
      <DailyRewardsBottomSheet
        isOpen={state.isDailyRewardsOpen}
        onClose={actions.closeDailyRewards}
        state={dailyState}
        actions={dailyActions}
        username={state.userPseudo ?? '—'}
      />

      {/* Maintenance Mode Overlay - blocks landing page when active */}
      {state.isMaintenanceActive && <MaintenancePage />}
    </div>
  );
}
